import React from "react";

/**
 * ModelIcon - brand mark for each AI model.
 *
 * Marks are drawn inline as SVG so they inherit currentColor and need no
 * network request. Used nominatively to identify which model produced a given
 * analysis. Presentation only.
 */

export const MODEL_BRAND = {
  ChatGPT: { color: "#10A37F", label: "ChatGPT" },
  Claude: { color: "#D97757", label: "Claude" },
  Gemini: { color: "#4285F4", label: "Gemini" },
  Perplexity: { color: "#20808D", label: "Perplexity" },
  Grok: { color: "#111111", label: "Grok" },
};

const PATHS = {
  // OpenAI mark
  ChatGPT:
    "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4021-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.0379-.0568V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
  // Anthropic mark
  Claude:
    "M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5527h3.7442L10.5363 3.541Zm-.3712 10.2286 2.2914-5.9456 2.2914 5.9456Z",
  // Gemini four-point star
  Gemini:
    "M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.305 14.305 0 0 0 12 12 14.305 14.305 0 0 0-12 12",
  // Perplexity mark
  Perplexity:
    "M11.25 2.4 4.5 8.02V6.3h-1.5v2.96l-1.72 1.43.96 1.15L3 11.6v6.9h4.5v3.1l4.5-3.75V22.5h1.5v-4.65l4.5 3.75v-3.1H21v-6.9l.76.24.96-1.15L21 9.26V6.3h-1.5v1.72L12.75 2.4v6.9h-1.5V2.4ZM12.75 10.8H18l1.5 1.25v4.95h-3v-2.9l-3.75-3.13v-.17Zm-1.5 0v.17L7.5 14.1v2.9h-3v-4.95L6 10.8h5.25Zm1.5 5.28 3 2.5v.92l-3-2.5v-.92Zm-1.5 0v.92l-3 2.5v-.92l3-2.5Z",
  // Grok / xAI mark
  Grok:
    "M6.469 8.776 16.512 23h-4.464L2.005 8.776h4.464Zm-.004 7.9-2.233 3.164L2 23h4.465l2.232-3.163-2.232-3.161ZM22 2.5l-8.51 12.06 2.232 3.163L22 8.826V2.5Zm0 6.326V23h-3.694V13.9L22 8.826Z",
};

export default function ModelIcon({ model, size = 16, className = "", colored = false }) {
  const d = PATHS[model];
  if (!d) return null;
  const brand = MODEL_BRAND[model];
  return (
    <svg
      className={`model-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={colored && brand ? brand.color : "currentColor"}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  );
}
